/**
 * Edit name / phone / address / account number (old `handleUpdateDebt` App.tsx:21908-21976): the same-name prompt
 * first (D-017; another person with the identical name), then `PATCH /customers` with the user's own PIN (D-018
 * replaces the security code); `show_customer_delete_edit` + `edit_customers` (R-25). The name belongs to the person,
 * so both of a person's accounts show it; past entries keep their ids (R-18's stale name text is gone, D-017).
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DuplicateNameModal, EditCustomerModal, type EditCustomerValues } from "@faylasuf/ui";
import type { CustomerAccountRow, CustomerNameCheck, UpdateCustomerBody } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { useToast } from "../../app/toast.js";
import { kindLabel } from "./format.js";

export function EditContainer({ row, themeColor, onClose, onViewStatement }: { row: CustomerAccountRow; themeColor: string; onClose: () => void; onViewStatement: (id: string) => void }) {
  const { t, source } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const pin = usePinGate();
  const qc = useQueryClient();
  const [values, setValues] = useState<EditCustomerValues>({ name: row.name, phone: row.phone ?? "", address: row.address ?? "", accountNumber: row.accountNumber });
  const [dup, setDup] = useState<CustomerNameCheck["matches"] | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  const save = async () => {
    const body: UpdateCustomerBody = {
      id: row.id,
      ...(values.name.trim() !== row.name ? { name: values.name.trim() } : {}),
      ...(values.phone.trim() !== (row.phone ?? "") ? { phone: values.phone.trim() || null } : {}),
      ...(values.address.trim() !== (row.address ?? "") ? { address: values.address.trim() || null } : {}),
      ...(values.accountNumber.trim() !== row.accountNumber ? { accountNumber: values.accountNumber.trim() } : {}),
    };
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.updateCustomer(body, proof.pin ?? ""), { message: t("cust.pin_edit", { name: row.name }), allowSupervisor: false });
      if (!res) return;
      toast(t("toast.saved"), "success");
      void qc.invalidateQueries({ queryKey: ["customers"] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      onClose();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const name = values.name.trim();
    if (!name) return;
    if (name !== row.name) {
      try {
        const check = await source.customerNameCheck(name);
        const others = check.matches.filter((m) => m.personId !== row.personId);
        if (others.length > 0) {
          setDup(others);
          return;
        }
      } catch (e) {
        fail(e);
        return;
      }
    }
    await save();
  };

  return (
    <>
      {!dup && (
        <EditCustomerModal values={values} onChange={(p) => setValues((v) => ({ ...v, ...p }))} themeColor={themeColor} busy={busy} onSubmit={() => void submit()} onClose={onClose} />
      )}
      {dup && (
        <DuplicateNameModal
          name={values.name.trim()}
          matches={dup.map((m) => ({ id: m.id, name: m.name, kindLabel: kindLabel(t, m.kind), accountNumber: m.accountNumber, archived: m.archived }))}
          onViewStatement={onViewStatement}
          onChangeName={() => setDup(null)}
          onCancel={() => setDup(null)}
        />
      )}
      {pin.dialog}
    </>
  );
}
